import {
  compileRows,
  fromCsvArrayBuffer,
  fromCsvFile,
} from '@/modules/CatalogOverplotModule/CatalogOverplot/compileRows'
import { MarkerStyle } from '@/modules/CatalogOverplotModule/Marker/marker'
import { IPreviewResponse } from '@/modules/ssp_database/CasModule/cas'
import { KdTree } from '@/utils/kd_tree'
import { V3, V4 } from '@/utils/math'
import { Component, Vue, Watch } from "vue-property-decorator"

interface ICatalogSource {
  name: string
  header: string[]
  coord3: V3[]
  rows: any[]
}

@Component
export class CatalogOverplot extends Vue {
  name!: string
  rows!: any[]
  header!: string[]
  coord3!: V3[]
  editable = false

  show = true
  showTable = false
  showChart = false
  color = [0, 1, 0, 0.25] as V4
  marker = MarkerStyle.CIRCLE
  tableFilterFunc = null as null | ((i: number) => boolean)
  chartSelectedIndices: number[] | null = null
  selected: number[] = []

  @Watch('tableFilterFunc')
  onTableFilterFuncChange() {
    this.chartSelectedIndices = null
  }

  get filteredIndices() {
    if (this.chartSelectedIndices) {
      return this.chartSelectedIndices
    } else {
      return this.tableFilterFunc ?
        this.rows.map((row, i) => i).filter((i) => this.tableFilterFunc!(i)) :
        this.rows.map((row, i) => i)
    }
  }

  get filteredCoord3() {
    return this.filteredIndices.map((i) => this.coord3[i])
  }

  get selectedCoord3() {
    return this.selected.map((i) => this.coord3[i])
  }

  get spatialIndex(): KdTree<3, number> {
    return new KdTree(this.filteredIndices, (i) => this.coord3[i])
  }

  get hasChart() {
    return this.header.length >= 2
  }

  static make({ name, rows, coord3, header }: ICatalogSource, options: { editable?: boolean } = {}) {
    const editable = !!options.editable
    if (!editable) {
      Object.freeze(rows)
      Object.freeze(coord3)
    }
    return new CatalogOverplot({
      data: { name, rows, coord3, header, editable },
    })
  }

  get length() {
    return this.rows.length
  }

  static async fromCsvFile(file: File) {
    const name = file.name
    const { header, rows, coord3 } = await fromCsvFile(file)
    return this.make({ name, header, coord3, rows })
  }

  static async fromCsvArrayBuffer(name: string, ab: ArrayBuffer) {
    const { header, rows, coord3 } = await fromCsvArrayBuffer(name, ab)
    return this.make({ name, header, coord3, rows })
  }

  static fromCasPreview(name: string, preview: IPreviewResponse["preview"]) {
    const { header, coord3, rows } = compileRows([preview.fields].concat(preview.rows))
    return this.make({ name, header, coord3, rows })
  }
}
